(ns rothko.editor.edit.paths
  (:require [com.rpl.specter :as sp]
            [rothko.editor.document.navigator :refer [selectionnav]]
            [rothko.editor.document.navigators :as n]
            [rothko.editor.document.paths :as document-paths]
            [rothko.editor.actions :as actions]
            [rothko.editor.space.paths :as space-paths]
            [rothko.editor.util :as util]
            [rothko.editor.word.transform :as word-transform]))

(def ^{:doc "Is it an edit token?"} EDIT?
  [:type
   (sp/pred= :edit)])

(def EDIT-OR-SPACE-EDIT? (sp/multi-path EDIT? space-paths/SPACE-EDIT?))

(def create*
  (selectionnav
   [chars]
   (transform*
    [_ {:keys [start-index] :as selection} structure next-fn]
    (sp/transform
     (sp/multi-path
      [:script
       (sp/before-index start-index)
       (sp/terminal-val
        {:word chars
         :type :edit})]
      (n/expand-selection
       {:shift-start-offset (count chars)
        :shift-end-offset (count chars)}))
     next-fn
     structure))))

(defn create
  "Inserts a new :edit token (with `chars`) before the token at the beginning of
  the current editor selection. Selection is moved to the end of the new token."
  [chars]
  (sp/with-fresh-collected
    [(sp/collect-one :selection)
     (create* chars)]))

(def
  ^{:doc "A path which will remove all (space-)edit tokens."}
  remove-edits
  (sp/with-fresh-collected
   [(sp/collect-one :selection)
    :script
    n/selection-srange
    sp/ALL
    (sp/selected? EDIT-OR-SPACE-EDIT?)
    (sp/terminal-val
     sp/NONE)]))

(def create-from-selection*
  (selectionnav
   [chars space-before-replacement? space-edits? remove-edits?]
   (transform*
    [_ {:keys [start-index end-index] :as selection} structure _]
    (sp/multi-transform
     [(sp/collect-one
       :selection)

      (sp/collect-one
       :script)

      (sp/collect-one
       n/surrounds)

      (sp/multi-path

       [:script
        n/selection-srange
        (sp/terminal
         (fn [_ _ [start-word-fragment end-word-fragment] selected]
           (let [word (str start-word-fragment chars end-word-fragment)
                 tokens (-> (n/merge-tokens selected)
                            (n/edit-tokens-from-merged-token word space-edits?))]
             (->> (if (< 0 (count word))
                    [selected
                     (when space-before-replacement?
                       [{:word " "
                         :type :space}])
                     tokens]
                    [selected])
                  (sp/select [sp/ALL sp/ALL])))))]

       (word-transform/mark-as-edited start-index end-index)

       (if remove-edits? remove-edits [sp/DISPENSE actions/no-op])

       [:selection
        (sp/terminal
         (fn [_ script [start-word-fragment end-word-fragment] {:keys [start-index end-index]}]
           (let [tokens-before-caret (n/edit-tokens-from-merged-token
                                      {} (str start-word-fragment chars) space-edits?)
                 word (str start-word-fragment chars end-word-fragment)
                 removed-edit-count (if remove-edits?
                                      (->> structure
                                           (sp/multi-transform remove-edits)
                                           :script
                                           count
                                           (- (count script)))
                                      0)
                 adjusted-end-index (- end-index removed-edit-count)
                 new-offset (-> tokens-before-caret last :word count)
                 new-index (if (and (zero? (count word))
                                    (= start-index (dec (count script))))
                             adjusted-end-index
                             (+ adjusted-end-index
                                (if space-before-replacement? 1 0)
                                (max
                                 ;; Assume at least 1 for the current backspace tests.
                                 ;; However, I'm not sure if the current behaviour of caret after backspace is correct.
                                 ;; Shouldn't it be moved to the left of the tokens marked as edited?
                                 1
                                 (count tokens-before-caret))))]

             (assoc selection
                    :start-index new-index
                    :start-offset new-offset
                    :end-index new-index
                    :end-offset new-offset))))])]
     structure))))

(defn create-from-selection
  "Replaces the selected text (on offset level) with `chars` by marking the
  selected tokens as edited and inserting a new edit after them, separated
  by a space as an option. Places the caret after the `chars`. Optionally can
  remove preexisting edit token(s) from the selected token span."
  [chars {:keys [space-before-replacement? space-edits? remove-edits?]}]
  (sp/with-fresh-collected
   [(sp/collect-one :selection)
    (create-from-selection* chars space-before-replacement? space-edits? remove-edits?)]))
